/**
 * 海报风格选项 + tone 注入文本 — 2026-06-01 重构
 *
 * 设计来源:融合三份物料 —— 现有 20 风格 + 业务场景 15 风格(AI/GEO/SaaS/企业/门店)
 * + 通用画风字典(水墨/水彩/油画/素描/版画/超现实/蒸汽朋克/像素/二次元...)
 * + 二次元/动画细分(吉卜力/少年漫/少女漫/赛璐珞/卡通渲染/厚涂/迪士尼2D/扁平卡通/黏土定格)。
 *
 * tone 是「单一事实源」:用户选中后写入 normalize 的 context.user_style(归整层据此
 * 拆 visual_style.tone/color/keywords,prompts.py 强制保留全部细节),也直接拼进出图
 * prompt 的【风格】段。所以 tone 写详细 + 带英文 art-style anchor,AI 优化与出图都更准
 * (gpt-image-2/万相 对英文风格标签训练密度远高于中文)。
 *
 * tone 写作公式(2026-07-12 风格线优化批,七要素协议):气质定调 + 色板句(基底/辅助/
 *   小面积强调) + 光影材质句(单一光源/真实质感) + 构图纪律句(单一视觉锤/留白,按风格
 *   惯例) + 5-8 个英文锚点(媒介/流派/时代,不上在世艺术家名)。全部正面表述,不写否定
 *   名词(会被模型字面化反向触发,V28.3 教训)。**不写** logo/二维码/电话/位置/序号/繁体,
 *   不写具体画面物件(归整层会把物件挪去 visual_scene,tone 只承载审美调性)。
 *   与 ai 侧 app/modules/advisor/poster_styles.py 双 SOT 同步。
 *
 * group:business(业务核心,排前+侧栏热门) / anime(二次元·动画) / creative(创意画风) / more(媒介技法+长尾)。
 * key:稳定锚 —— 样例图文件名 public/style-samples/{key}.webp + 选中态标识(label 是
 *     中文会改,不能当 key)。
 * blurb:一句话人话,画廊里显示,降低术语门槛。
 */

export interface StyleOption {
  /** 注入 LLM 的 tone 文本(rich, 中文具象 + 英文 anchor)。null = 自由发挥不注入。 */
  tone: string | null;
  /** UI 显示短名(4-6 字) */
  label: string;
  /** 稳定 key:样例图文件名 + 选中标识。自由发挥/自定义无 key。 */
  key?: string;
  /** 一句话描述,画廊里显示 */
  blurb?: string;
  /** 分组 */
  group?: StyleGroupKey;
}

export type StyleGroupKey = "business" | "anime" | "creative" | "more";

export interface StyleGroupMeta {
  key: StyleGroupKey;
  title: string;
  hint: string;
}

/** 分组元数据(画廊 sticky 标题用),顺序即展示顺序 */
export const STYLE_GROUPS: readonly StyleGroupMeta[] = [
  { key: "business", title: "业务核心", hint: "AI / GEO / SaaS / 企业服务 / 门店常用" },
  { key: "anime", title: "二次元 · 动画", hint: "漫画 / 动画 / 3D 角色 / 卡通渲染" },
  { key: "creative", title: "创意画风", hint: "国风 / 文化 / 亚文化主视觉" },
  { key: "more", title: "更多画风", hint: "绘画媒介 / 复古 / 长尾风格" },
];

/** 自由发挥(默认):tone=null,AI 据内容自判调性。常驻首位,不进分组网格。 */
export const FREE_STYLE: StyleOption = { tone: null, label: "自由发挥" };

/**
 * 39 个具象风格。group=business 的 8 个会出现在侧栏「热门」横排;全部 39 进画廊。
 */
export const PRESET_STYLES: readonly StyleOption[] = [
  // ── 业务核心(8) ──────────────────────────────────────────────
  {
    key: "ai-future",
    label: "科技AI未来",
    group: "business",
    blurb: "蓝紫光效·AI中枢·数据流,科技未来感",
    tone: "科技未来感的高级克制:深空蓝为基底、辅以石墨深灰,电光紫与青色高光只作小面积强调;半透明玻璃拟态与拉丝金属构成主要材质,冷色定向主光配轻微轮廓光、光源方向统一;构图如高端科技广告主视觉,单一视觉锤、大量呼吸留白,至少两个角落保持安静。futuristic tech key visual, holographic glassmorphism, brushed metal, directional cool lighting, restrained neon accent, premium sci-fi campaign",
  },
  {
    key: "corporate",
    label: "企业商务",
    group: "business",
    blurb: "深蓝稳重·卡片图表,B2B 专业可信",
    tone: "现代企业品牌广告的干净可信:深海军蓝配大面积中性白与暖灰,品牌蓝仅作小面积强调;以真实商业摄影质感为主,辅以细腻磨砂玻璃与纸感,柔和定向棚拍主光、阴影自然衰减;非对称编辑式网格构图,单一视觉锤、留白充分、层级清晰稳重。modern corporate campaign, editorial art direction, restrained premium photography, asymmetrical grid, soft studio lighting, quiet confidence",
  },
  {
    key: "dashboard",
    label: "数据大屏",
    group: "business",
    blurb: "仪表盘·图表·监控屏,秀技术能力",
    tone: "数据科技的酷感秩序:石墨黑深色基底,霓虹蓝绿数据光只作克制点缀、发光集中在少数重点;深色界面质感配细腻发光描边,冷色单一主光、暗部保持中性干净;信息图形分层排布、疏密分明,重点区域一眼可辨。data-tech aesthetic, dark UI, structured HUD, neon blue-green accent, clean glow, orderly layered composition",
  },
  {
    key: "scifi",
    label: "科幻大片",
    group: "business",
    blurb: "宇宙星河·光束飞船,大概念发布",
    tone: "科幻电影般的宏大氛围:冷蓝紫深空为基底,暖金光晕只作单一强调;体积光克制使用、光源方向明确,材质保留真实颗粒与大气透视;构图纵深开阔、单一宏大主体,画面上部保留通透空间,层次深远而不拥挤。cinematic sci-fi key visual, volumetric light used sparingly, atmospheric depth, cool deep-space palette, single warm gold accent, grand composition",
  },
  {
    key: "minimal",
    label: "高级极简",
    group: "business",
    blurb: "大留白·单主视觉,看起来贵有品牌感",
    tone: "高端极简的克制贵气:米白或纯黑大面积留白为基底,金灰只作细部点缀;单一主视觉居中或偏轴,负空间占画面一半以上;材质精致哑光、光线柔和均匀,细节少而准,每个元素都有存在理由。luxury minimalist, generous negative space, editorial restraint, matte premium texture, single visual anchor, swiss-inspired discipline",
  },
  {
    key: "hard-sell",
    label: "强转化广告",
    group: "business",
    blurb: "大字撞色·利益前置,强营销抓眼球",
    tone: "高能促销广告的直接冲击:一组高饱和撞色统一全画面(主色+对比色+一个强调色),热闹但色板收在三色内;大色块平涂配强对比光,视觉锤唯一、对角线动势明确;画面密度中高、节奏直接,重点只有一个、层级分明不乱。high-impact promo key visual, bold contrasting flat colors, unified three-color palette, diagonal energy, single dominant focal point, mature e-commerce campaign",
  },
  {
    key: "flat-illustration",
    label: "扁平插画",
    group: "business",
    blurb: "扁平人物·流程箭头,讲清复杂功能",
    tone: "扁平矢量插画的亲和清爽:一组明快撞色控制在三色系内,大面积干净底色;圆角几何造型、平涂色块配少量简洁描边,光影用色块明暗两档表达;构图疏密有致、单一主场景,装饰元素最多一两组。flat vector illustration, clean bright palette, rounded geometry, two-tone shading, friendly modern, generous breathing room",
  },
  {
    key: "photography",
    label: "写实摄影",
    group: "business",
    blurb: "实拍门店/团队/产品,真实可信",
    tone: "真实商业摄影质感:自然光或暖调棚光、单一明确光源,浅景深、焦点唯一;色调干净通透、饱和克制、阴影中性;保留真实材质细节与轻微不完美,构图遵循三分法、主体外留足呼吸空间,真实可信不摆拍。commercial photography, natural directional light, shallow depth of field, rule of thirds, authentic texture, restrained color grading",
  },

  // ── 二次元 · 动画(14) ─────────────────────────────────────────
  {
    key: "anime",
    label: "日系动漫",
    group: "anime",
    blurb: "通透天空·柔光治愈,新海诚向",
    tone: "日系动漫的清新治愈:通透天空蓝与低饱和粉彩为主,一个暖色作小面积强调;柔和顶光与干净云影,赛璐璐平涂配细腻光斑;画面留白通透、主体唯一、远近层次分明,气氛安静治愈。japanese anime illustration, luminous sky aesthetic, pastel palette, soft top light, cel shading, gentle healing mood",
  },
  {
    key: "ghibli",
    label: "吉卜力",
    group: "anime",
    blurb: "宫崎骏手绘水彩·田园治愈",
    tone: "吉卜力式手绘水彩的温暖田园:草绿、天蓝与暖米色为主,朱红只作小点缀;可见手绘笔触与水彩纸纹,柔和自然光、影子透明干净;构图开阔宁静、单一叙事主体,大片天空或原野留出呼吸空间。Studio Ghibli style, hand-painted watercolor, pastoral warmth, soft natural light, visible brushwork, tranquil open composition",
  },
  {
    key: "shonen",
    label: "热血少年漫",
    group: "anime",
    blurb: "粗线条·速度线·能量光效·燃",
    tone: "热血少年漫的张力:高饱和主色配黑白强对比,能量光效集中在单一焦点;粗利落墨线、放射速度线向主体汇聚,网点阴影克制使用;构图对角动势、主体占比大、背景为气势让路,燃而不乱。shonen manga, bold ink linework, radial speed lines, focused energy glow, high-contrast palette, dynamic diagonal composition",
  },
  {
    key: "shoujo",
    label: "少女漫",
    group: "anime",
    blurb: "纤细线·花瓣闪光·梦幻粉彩",
    tone: "少女漫的唯美柔软:粉彩低饱和色系,珠光白与淡金只作细部闪光;纤细线条、柔焦光晕,花瓣状光斑点到为止;构图留白梦幻、主体唯一,气氛浪漫安静。shoujo manga, delicate linework, dreamy soft focus, pastel palette, sparkling accents used sparingly, romantic negative space",
  },
  {
    key: "cel-3d",
    label: "卡通渲染",
    group: "anime",
    blurb: "二次元转3D·游戏CG级·华丽",
    tone: "卡通渲染的精致立体:清晰墨线包裹两段式赛璐璐阴影,色彩饱满但色板统一;单一主光源配轻柔轮廓光,华丽光效集中在主体;构图英雄式居中或三分,背景简洁衬托主体。cel-shaded 3D, anime toon shading, clean outline, two-tone shadow, hero-centered composition, polished AAA anime game CG lighting",
  },
  {
    key: "thick-paint",
    label: "厚涂插画",
    group: "anime",
    blurb: "厚涂笔触·半写实·pixiv精致",
    tone: "日系厚涂的半写实精致:饱满体积感笔触、细腻的皮肤与材质过渡,插画大赛级完成度;电影氛围光、单一主光源、暗部透气不发黑;色板统一克制,主体清晰、背景虚实分层。semi-realistic anime, thick-paint painterly, volumetric brushwork, cinematic single-source lighting, refined finish, layered depth",
  },
  {
    key: "comic",
    label: "美式漫画",
    group: "anime",
    blurb: "粗描边·网点·爆炸字,波普张力",
    tone: "美式漫画的波普张力:黄红蓝高饱和三原色为主、黑色粗描边压场;网点纹理与色块平涂,光影两档硬切;构图戏剧化、主体夸张前冲,爆发感集中在单一焦点,复古印刷质感真实。American comic, Ben-Day dots, bold black outlines, pop art primaries, dramatic single focal point, retro print texture",
  },
  {
    key: "manhua-drama",
    label: "国漫漫剧",
    group: "anime",
    blurb: "国漫厚涂·戏剧光影·漫剧分镜",
    tone: "国漫厚涂漫剧的戏剧感:精致人物级厚涂,戏剧化明暗对比、单一强光源塑形;色彩饱和但统一在一组主色系,暗部干净;构图如剧集关键帧,情绪聚焦单一主体,背景虚化让位。Chinese manhua thick-paint, dramatic cinematic lighting, donghua key frame, unified saturated palette, emotional single-subject focus",
  },
  {
    key: "pixar-3d",
    label: "皮克斯3D",
    group: "anime",
    blurb: "皮克斯式3D角色·软萌大眼·蜡质肌肤",
    tone: "皮克斯式 3D 动画的软萌:圆润造型、温润蜡质皮肤与次表面散射,材质真实细腻;柔和电影布光、单一暖调主光,阴影柔和干净;构图以角色为绝对中心,背景简洁景深虚化,情绪温暖可爱。Pixar-style 3D animation, subsurface scattering, soft cinematic key light, rounded adorable forms, warm palette, shallow depth backdrop",
  },
  {
    key: "3d-toy",
    label: "3D潮玩",
    group: "anime",
    blurb: "3D软萌IP·发光图标,年轻化记忆点",
    tone: "3D 潮玩的轻松萌感:糖果色系统一色板、圆润光滑造型配发光高光点缀;柔和影棚光、明暗过渡干净;单一 IP 主体占画面中心,底色简洁纯净,细节集中在主体本身。3D designer toy render, glossy candy colors, soft studio light, single cute mascot focus, clean backdrop, collectible finish",
  },
  {
    key: "disney-2d",
    label: "迪士尼2D",
    group: "anime",
    blurb: "手绘2D·圆润优雅·童话感",
    tone: "迪士尼式经典手绘 2D 的童话感:流畅优雅的曲线造型,清透水彩上色、温暖明亮的金黄与天蓝色系;柔和舞台式主光,画面如动画电影定格;构图叙事感强、主体唯一,背景层次柔和退后。classic Disney-style 2D, hand-drawn elegance, watercolor wash, warm fairytale palette, stage-like soft light, storybook composition",
  },
  {
    key: "western-cartoon",
    label: "欧美扁平卡通",
    group: "anime",
    blurb: "粗描边·撞色·夸张搞怪",
    tone: "欧美扁平卡通的活泼幽默:高饱和撞色平涂、粗黑描边,色板收在三四色内;造型夸张搞怪、动作张力大;光影极简两档,背景大色块干净,笑点集中在单一主角。western cartoon, bold flat colors, thick outlines, exaggerated playful character, simple two-tone shading, clean color-block backdrop",
  },
  {
    key: "claymation",
    label: "黏土定格",
    group: "anime",
    blurb: "黏土质感·定格动画·手作温暖",
    tone: "黏土定格的手作温暖:可见捏痕与指纹的黏土肌理,材质真实不完美;柔和影棚光、微距景深,色彩温润低饱和;构图如定格动画一帧,圆润敦实的单一主体,微缩场景感十足。claymation stop-motion, plasticine texture, handmade imperfections, soft macro lighting, miniature scene, charming rounded forms",
  },
  {
    key: "mecha",
    label: "机甲高达",
    group: "anime",
    blurb: "巨型机甲·金属装甲·硬核科幻",
    tone: "日系机甲的硬核科幻:金属装甲的真实反光与磨损细节,冷灰基底配单一警示色点缀;硬朗定向光、高对比但阴影干净;低角度仰视构图增强体量感,单一机体为绝对主角,背景烟尘氛围克制。mecha anime, Gundam-inspired, weathered metal armor, hard directional light, low-angle heroic composition, restrained battle atmosphere",
  },

  // ── 创意画风(9) ──────────────────────────────────────────────
  {
    key: "guochao",
    label: "国潮新中式",
    group: "creative",
    blurb: "青绿山水·描金印章,东方国潮",
    tone: "当代新中式的东方韵味:米白宣纸底、墨黑与低饱和朱砂为主,描金只作极少量工艺点缀;宣纸纤维与淡墨晕染质感,光线柔和平面化;构图借中国画疏密留白、单一象征主体,传统纹样最多一种且置于边缘。contemporary guochao, xuan paper texture, ink wash restraint, cinnabar and gold accent, poetic negative space, modern oriental editorial",
  },
  {
    key: "ink-wash",
    label: "水墨意境",
    group: "creative",
    blurb: "墨色晕染·大留白,禅意素雅",
    tone: "纯水墨写意的禅意素雅:浓淡墨色五阶层次,纸白即留白、占画面大半;笔触洒脱、晕染自然通透;构图极简、一个主意象,气韵疏朗安静,水墨颗粒与纸纹真实。Chinese ink wash, sumi-e, tonal ink gradation, vast paper-white negative space, single poetic motif, zen restraint",
  },
  {
    key: "cyberpunk",
    label: "赛博朋克",
    group: "creative",
    blurb: "霓虹雨夜·故障感,赛博未来城",
    tone: "电影级赛博都市的成熟质感:深靛蓝与石墨黑为基底,单一电光青或洋红强调、面积克制;雨湿金属与磨砂玻璃材质,保留磨损与雾气;侧逆光来自画面内可解释的城市光源,体积雾有限;构图电影纵深、非对称重心,主体清晰、背景层次丰富而有序。cinematic cyberpunk, neo-noir city, wet metal reflections, controlled neon accent, directional rim light, atmospheric depth",
  },
  {
    key: "vaporwave",
    label: "蒸汽波",
    group: "creative",
    blurb: "石膏像·网格·撞色,80s迷幻",
    tone: "蒸汽波的复古数码梦境:粉紫为主、青色点缀的固定三色对撞;渐变天空与网格透视地平线,单一古典意象为主体的对称构图;轻微 VHS 颗粒与柔光质感,留白开阔,怀旧而不杂乱。vaporwave aesthetic, retro 80s digital dream, pink-purple gradient sky, perspective grid, single classical motif, soft VHS grain",
  },
  {
    key: "steampunk",
    label: "蒸汽朋克",
    group: "creative",
    blurb: "黄铜齿轮·维多利亚机械,复古工业",
    tone: "蒸汽朋克的机械复古:暖棕琥珀与黄铜金为主、墨绿点缀;黄铜齿轮与做旧皮革材质,包浆磨损真实;暖调侧光如煤气灯,阴影浓郁但透气;构图维多利亚式对称或单主体特写,机械细节密而有序、层级分明。steampunk, victorian machinery, brass and leather, warm gaslight, aged patina texture, ornate yet ordered composition",
  },
  {
    key: "y2k",
    label: "Y2K千禧",
    group: "creative",
    blurb: "铬金属·果冻气泡,千禧未来怀旧",
    tone: "Y2K 千禧数码美学:银色铬金属渐变为主,蓝粉紫高光点缀、面积克制;液态金属与果冻质感、塑料光泽真实通透;高调明亮布光、反射干净;单一主体居中呈现,背景通透简洁,未来怀旧感统一。Y2K chrome aesthetic, liquid metal, frutiger aero, jelly gloss, clean high-key lighting, single centered subject",
  },
  {
    key: "pixel",
    label: "像素复古",
    group: "creative",
    blurb: "像素点阵·扫描线,复古游戏感",
    tone: "8/16 位像素游戏的复古情怀:八至十六色的有限色板统一全画面,点阵造型棱角分明;抖动渐变与 CRT 扫描线质感克制使用;构图如游戏关键画面,单一主角清晰,背景分层卷轴感。pixel art, 16-bit retro game, limited palette, crisp dithering, CRT scanline hint, layered parallax scene",
  },
  {
    key: "surreal",
    label: "超现实",
    group: "creative",
    blurb: "梦境漂浮·错位想象,概念主视觉",
    tone: "超现实主义梦境:柔和渐变天空为底,低饱和暖灰配一个记忆点色;错位与失重感的单一概念意象,尺度反差制造想象;光线柔和方向一致、影子写实,让荒诞可信;构图极简开阔、大量负空间。surrealism, dreamlike single concept, soft gradient sky, scale contrast, consistent realistic shadows, expansive negative space",
  },
  {
    key: "wuxia",
    label: "国风武侠",
    group: "creative",
    blurb: "白衣侠客·刀光剑影·江湖意境",
    tone: "国风武侠的江湖大气:青灰冷调为主、一点朱红点睛;薄雾水墨氛围,墨色飞溅笔意克制;电影级侧逆光穿雾,体积光有限;构图远景大留白、单一主体剪影,意境疏朗苍茫。wuxia cinematic, ink splash restraint, misty atmosphere, cool grey palette with vermilion accent, silhouette focus, vast negative space",
  },

  // ── 更多画风(8) ──────────────────────────────────────────────
  {
    key: "hk-90s",
    label: "港风90s",
    group: "more",
    blurb: "胶片颗粒·霓虹招牌,港片复古",
    tone: "九十年代港片的复古胶片味:暖调做旧色彩,霓虹红黄蓝招牌光只作局部点缀;真实胶片颗粒与轻微色偏,市井夜色氛围;单一人物或街景主体,浅景深、光源来自画面内霓虹,构图电影感强。90s Hong Kong cinema, film grain, neon signage glow, warm nostalgic grading, shallow depth, hong kong new wave mood",
  },
  {
    key: "collage",
    label: "杂志拼贴",
    group: "more",
    blurb: "撕纸·套色错位,杂志拼贴粗粝",
    tone: "印刷杂志撕纸拼贴的粗粝文艺:手撕毛边层叠、套色错位的 risograph 印刷质感;色板收在三色套印内,纸纹与油墨颗粒真实;拼贴自由但重心唯一,元素两三组、留白呼吸,zine 感而不脏乱。magazine collage, risograph misprint, torn paper edges, three-color overprint, tactile ink grain, curated eclectic layout",
  },
  {
    key: "linocut",
    label: "手作版画",
    group: "more",
    blurb: "木刻肌理·牛皮纸,手作文艺",
    tone: "手作版画的文艺温度:牛皮纸暖米底,单色或双色油墨;木刻凿痕肌理粗犷有力、线条方向统一;构图对称稳重或单一主纹样,负空间即纸色,手工印压的轻微不均匀真实可感。linocut woodblock print, hand-carved texture, kraft paper base, single-ink discipline, bold carved lines, artisanal imperfection",
  },
  {
    key: "oil-painting",
    label: "古典油画",
    group: "more",
    blurb: "厚涂笔触·古典光影,油画高级感",
    tone: "古典油画的高级感:暖金褐色调为主、深棕阴影,一个冷色小点缀;厚涂笔触与画布肌理真实,伦勃朗式单侧主光、暗部透明;构图古典稳定、单一主体沐光,背景深沉退后,静穆优雅。classical oil painting, impasto brushwork, Rembrandt lighting, warm umber palette, canvas texture, dignified single-subject composition",
  },
  {
    key: "sketch",
    label: "素描手绘",
    group: "more",
    blurb: "铅笔排线·草图感,清雅手绘",
    tone: "铅笔素描手绘的清雅:单色石墨从浅灰到深黑的完整灰阶;排线方向统一、明暗过渡细腻,纸张纹理可见;构图学院派稳健、单一主体精细刻画,四周留白渐虚,安静专注。pencil sketch, graphite cross-hatching, tonal discipline, paper grain, academic single-subject study, fading white margins",
  },
  {
    key: "watercolor",
    label: "清透水彩",
    group: "more",
    blurb: "颜料晕染·通透清新,水彩雅致",
    tone: "清透水彩的雅致:湿画法颜料自然流动与渗色,浅淡通透、低饱和色系加一个清亮点缀;纸白留白即光,构图疏朗、单一主体,边缘虚实相间,清新不腻。watercolor wet-on-wet, translucent color bleed, paper-white highlights, airy negative space, fresh restrained palette, soft blooming edges",
  },
  {
    key: "maximalist-infographic",
    label: "极繁信息图",
    group: "more",
    blurb: "高密度·模块引线,信息图极繁",
    tone: "极繁信息图的密集张力:孟菲斯式撞色但色板固定在四五色内,高密度模块化排布;几何装饰与引线密而有序、对齐严格,每个模块边界清晰;视觉重心唯一,热闹但有骨架、密而处处可读。infographic maximalism, Memphis palette discipline, dense modular grid, ordered busy layout, clear visual anchor, information-rich energy",
  },
  {
    key: "doodle",
    label: "儿童涂鸦",
    group: "more",
    blurb: "蜡笔稚拙·亮色童趣,儿童涂鸦",
    tone: "蜡笔稚拙手绘的童趣:大块亮色平涂、色板明快收在四色内;歪扭天真的线条、蜡笔颗粒质感真实;构图自由但主角唯一、画面留出纸白,快乐直接不堆砌。childlike crayon doodle, naive wobbly lines, bright limited palette, waxy texture, single cheerful subject, playful paper-white space",
  },
];

/** 自由发挥 + 39 预设。VideoBriefPanel 等老调用方继续用这个全集。 */
export const STYLES: readonly StyleOption[] = [FREE_STYLE, ...PRESET_STYLES];

/** 当前选中的 tone 是否是用户自定义文本(非 null 且不命中任何预设 tone)。 */
export function isCustomTone(selected: string | null): boolean {
  if (selected === null) return false;
  return !STYLES.some((s) => s.tone === selected);
}

/**
 * 把 selectedStyle(tone, rich)映射成简短风格名,用于回传 styleName(详情页「本次设置」
 * 展示,backend @Size 限长,不能塞 rich tone)。预设 → label;自定义 → 原文截断 40;
 * null/自由发挥 → null。prompt 里【风格】注入仍用完整 tone。
 */
export function styleLabelForTone(tone: string | null): string | null {
  if (!tone || !tone.trim()) return null;
  const preset = STYLES.find((s) => s.tone === tone);
  if (preset) return preset.label;
  return tone.trim().slice(0, 40);
}

/** 按 tone 找预设(用于回显选中态)。 */
export function findStyleByTone(tone: string | null): StyleOption | undefined {
  if (!tone) return undefined;
  return PRESET_STYLES.find((s) => s.tone === tone);
}

/** 样例图 URL(public/style-samples/{key}.webp)。无 key 返 undefined。 */
export function styleSampleUrl(key?: string): string | undefined {
  return key ? `/style-samples/${key}.webp` : undefined;
}

/** 确定性渐变占位(样例图未就绪时的 fallback 背景),按 key 字符散列选色对。 */
export function stylePlaceholderGradient(key?: string): string {
  const pairs = [
    ["#6366f1", "#a855f7"],
    ["#0ea5e9", "#22d3ee"],
    ["#f59e0b", "#ef4444"],
    ["#10b981", "#14b8a6"],
    ["#ec4899", "#8b5cf6"],
    ["#64748b", "#94a3b8"],
    ["#f43f5e", "#fb923c"],
    ["#3b82f6", "#6366f1"],
  ];
  let h = 0;
  const s = key || "x";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const [a, b] = pairs[h % pairs.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}
